/**
 * t413u: HR records stages 3 and 4 on a computer (Settings › People & access › HR records): payroll export, starters
 * and leavers, missing details with reminders, retention with a purge preview, and key rotation. A person's subject
 * access download and erasure sit on their HR record (HrPersonActions). Calm and compact: cards, muted text, no badges.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  canRemindNow, HR_ERASE_CONFIRM, HR_PAYROLL_FORMAT_LABEL, HR_PAYROLL_FORMATS, HR_PAYROLL_NOTE, HR_PAYROLL_WHO, HR_PAYROLL_WHO_LABEL, HR_RETENTION_FIELDS, HR_RETENTION_LIMITS,
  hrDate, hrKeyLine, hrLeaverLine, hrPayrollFileName, hrPayrollInputErrors, hrPurgeLine, hrRemindedLine, hrReportRange, hrRetentionErrors, hrStarterLine,
  type HrPayrollFormat, type HrPayrollWho, type HrRetention, type HrSettingsView,
} from '@tradeoutlet/shared';
import { useHrErasure, useHrMissing, useHrStartersLeavers } from '@tradeoutlet/data';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { Button, Card, Field, Input, Modal, Select, Spinner, Textarea, Toggle } from '@/components/ui';
import { ask } from '@/components/ui/confirm';
import { hrKey, refreshHr } from './hooks';
import { EditableHint } from '@/components/ui/EditableHint';

const saveFile = (name: string, text: string, type = 'text/csv;charset=utf-8') => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([text], { type }));
  a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
};
const when = (iso: string) => new Date(iso).toLocaleString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

function NameButton({ id, name, onOpenPerson }: { id: string; name: string; onOpenPerson: (id: string) => void }) {
  return <button type="button" className="min-w-0 truncate text-left font-medium hover:underline" onClick={() => onOpenPerson(id)}>{name}</button>;
}

// ------------------------------------------------------------------------------------------------ payroll
export function PayrollTab() {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const [format, setFormat] = useState<HrPayrollFormat>('standard');
  const [who, setWho] = useState<HrPayrollWho>('active');
  const [since, setSince] = useState('');
  const [busy, setBusy] = useState(false);
  const log = useQuery({ queryKey: hrKey(ws, 'exports'), queryFn: () => api.hr.exports(ws!), enabled: Boolean(ws), retry: false });
  const errs = hrPayrollInputErrors({ format, who, since: who === 'changed' ? since : null });
  const run = async () => {
    setBusy(true);
    try {
      const csv = await api.hr.payrollCsv(ws!, { format, who, since: who === 'changed' ? since : null });
      saveFile(hrPayrollFileName(format), csv.startsWith('\uFEFF') ? csv : '\uFEFF' + csv);
      toast('Payroll file downloaded', 'success');
      void refreshHr(ws);
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="hr-payroll">
      <Card title="Payroll export">
        <div className="grid max-w-2xl gap-3 sm:grid-cols-2">
          <Field label="Format"><Select value={format} onChange={e => setFormat(e.target.value as HrPayrollFormat)} data-testid="hr-payroll-format">
            {HR_PAYROLL_FORMATS.map(f => <option key={f} value={f}>{HR_PAYROLL_FORMAT_LABEL[f]}</option>)}</Select></Field>
          <Field label="Who"><Select value={who} onChange={e => setWho(e.target.value as HrPayrollWho)} data-testid="hr-payroll-who">
            {HR_PAYROLL_WHO.map(w => <option key={w} value={w}>{HR_PAYROLL_WHO_LABEL[w]}</option>)}</Select></Field>
          {who === 'changed' && <Field label="Changed since"><Input type="date" value={since} onChange={e => setSince(e.target.value)} data-testid="hr-payroll-since" /></Field>}
        </div>
        <p className="mt-3 max-w-2xl text-sm text-slate-500">{HR_PAYROLL_NOTE}</p>
        {errs.length > 0 && who === 'changed' && since && <p className="mt-2 text-sm text-rose-700" role="alert">{errs.join('. ')}</p>}
        <Button className="mt-3" size="sm" disabled={busy || errs.length > 0} onClick={() => void run()} data-testid="hr-payroll-download">{busy ? 'Preparing…' : 'Download CSV'}</Button>
      </Card>
      <Card title="Past exports">
        {log.isLoading ? <Spinner /> : !log.data ? <p className="text-sm text-slate-500">{errorMessage(log.error)}</p> : (
          <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800" data-testid="hr-exports">
            {log.data.map(r => (
              <li key={r.id} className="flex flex-wrap gap-x-3 py-2">
                <span className="font-medium">{r.kind === 'payroll' ? `Payroll${r.format ? ` (${r.format === 'brightpay' ? 'BrightPay' : 'standard'})` : ''}` : `HR data for ${r.subject_name ?? 'someone'}`}</span>
                <span className="text-slate-500">{r.kind === 'payroll' ? `${r.people} ${r.people === 1 ? 'person' : 'people'} · ` : ''}{r.created_by_name ?? 'Someone'} · {when(r.created_at)}</span>
              </li>
            ))}
            {!log.data.length && <li className="py-2 text-slate-500">No exports yet.</li>}
          </ul>
        )}
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ starters and leavers
export function ReportsTab({ onOpenPerson }: { onOpenPerson: (id: string) => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const [range, setRange] = useState(hrReportRange);
  const q = useHrStartersLeavers(ws, range.from, range.to);
  const csv = async () => {
    try { saveFile(`starters-and-leavers-${range.from}-to-${range.to}.csv`, await api.hr.startersLeaversCsv(ws!, range)); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  return (
    <Card title="Starters and leavers" actions={<Button size="sm" variant="ghost" onClick={() => void csv()} disabled={!q.data} data-testid="hr-reports-csv">Download CSV</Button>}>
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <Field label="From"><Input type="date" value={range.from} onChange={e => e.target.value && setRange({ ...range, from: e.target.value })} data-testid="hr-reports-from" /></Field>
        <Field label="To"><Input type="date" value={range.to} onChange={e => e.target.value && setRange({ ...range, to: e.target.value })} data-testid="hr-reports-to" /></Field>
      </div>
      {q.isLoading ? <Spinner /> : !q.data ? <p className="text-sm text-slate-500">{errorMessage(q.error)}</p> : (
        <div className="grid gap-6 lg:grid-cols-2" data-testid="hr-reports">
          <section>
            <h3 className="mb-1 text-sm font-semibold">Starters</h3>
            <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
              {q.data.starters.map(s => <li key={s.user_id} className="flex flex-col py-2"><NameButton id={s.user_id} name={s.display_name} onOpenPerson={onOpenPerson} />
                <span className="text-slate-500">{[s.location_name, hrStarterLine(s)].filter(Boolean).join(' · ')}</span></li>)}
              {!q.data.starters.length && <li className="py-2 text-slate-500">No one started.</li>}
            </ul>
          </section>
          <section>
            <h3 className="mb-1 text-sm font-semibold">Leavers</h3>
            <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
              {q.data.leavers.map(l => <li key={l.user_id} className="flex flex-col py-2"><NameButton id={l.user_id} name={l.display_name} onOpenPerson={onOpenPerson} />
                <span className="text-slate-500">{[l.location_name, hrLeaverLine(l)].filter(Boolean).join(' · ')}</span></li>)}
              {!q.data.leavers.length && <li className="py-2 text-slate-500">No one left.</li>}
            </ul>
          </section>
        </div>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------------------------------------ missing details
export function MissingTab({ settings, onOpenPerson }: { settings: HrSettingsView; onOpenPerson: (id: string) => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const q = useHrMissing(ws);
  const [busy, setBusy] = useState(false);
  const remind = async (ids: string[]) => {
    setBusy(true);
    try {
      const r = await api.hr.remind(ws!, ids);
      toast(r.sent ? `Reminder sent to ${r.sent === 1 ? '1 person' : `${r.sent} people`}` : 'Already reminded today', r.sent ? 'success' : 'info');
      void refreshHr(ws);
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  const due = (q.data ?? []).filter(r => canRemindNow(r.reminded_at));
  return (
    <Card title="Missing HR details" actions={settings.can_remind && due.length > 1 ? <Button size="sm" variant="ghost" disabled={busy} onClick={() => void remind(due.map(r => r.user_id))} data-testid="hr-remind-all">Remind all {due.length}</Button> : undefined}>
      {q.isLoading ? <Spinner /> : !q.data ? <p className="text-sm text-slate-500">{errorMessage(q.error)}</p> : (
        <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800" data-testid="hr-missing-list">
          {q.data.map(r => (
            <li key={r.user_id} className="flex items-center gap-3 py-2" data-testid={`hr-missing-${r.user_id}`}>
              <span className="flex min-w-0 flex-1 flex-col"><NameButton id={r.user_id} name={r.display_name} onOpenPerson={onOpenPerson} />
                <span className="truncate text-slate-500">{[r.location_name, hrRemindedLine(r)].filter(Boolean).join(' · ')}</span></span>
              {settings.can_remind && canRemindNow(r.reminded_at) && <Button size="sm" variant="ghost" disabled={busy} onClick={() => void remind([r.user_id])} data-testid={`hr-remind-${r.user_id}`}>Remind</Button>}
            </li>
          ))}
          {!q.data.length && <li className="py-2 text-slate-500">Everyone’s HR details are complete.</li>}
        </ul>
      )}
      <EditableHint as="p" id="people.hr.missing-reminders" className="mt-2 text-xs text-slate-500" default="Reminders go to the person’s bell and phone, once a day at most." />
    </Card>
  );
}

// ------------------------------------------------------------------------------------------------ retention and keys
export function RetentionTab({ settings }: { settings: HrSettingsView }) {
  return <div className="space-y-4">{settings.can_retention && <RetentionCard />}{settings.can_keys && <KeysCard />}</div>;
}

function RetentionCard() {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const q = useQuery({ queryKey: hrKey(ws, 'retention'), queryFn: () => api.hr.retention(ws!), enabled: Boolean(ws), retry: false });
  const [draft, setDraft] = useState<HrRetention | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (q.data && !draft) setDraft(q.data.retention); }, [q.data, draft]);
  if (q.isLoading || (q.data && !draft)) return <Spinner />;
  if (!q.data || !draft) return <p className="text-sm text-slate-500">{errorMessage(q.error)}</p>;
  const p = q.data;
  const errs = hrRetentionErrors(draft);
  const changed = JSON.stringify(draft) !== JSON.stringify(p.retention);
  const save = async () => {
    setBusy(true);
    try { await api.hr.saveRetention(ws!, draft); toast('Retention saved', 'success'); setDraft(null); void refreshHr(ws); } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  const purge = async () => {
    const n = p.due.reduce((a, i) => a + i.count, 0);
    if (!(await ask({ title: 'Remove these HR details now?', body: `${n} item${n === 1 ? '' : 's'} past their retention date will be removed for good, files included. This can’t be undone.`, confirmLabel: 'Remove now' }))) return;
    setBusy(true);
    try { const r = await api.hr.purge(ws!); toast(`Removed ${r.removed} item${r.removed === 1 ? '' : 's'}`, 'success'); void refreshHr(ws); } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  return (
    <>
      <Card title="How long HR details are kept">
        <div className="grid max-w-3xl gap-3 sm:grid-cols-2" data-testid="hr-retention">
          {HR_RETENTION_FIELDS.map(f => (
            <Field key={f.key} label={`${f.label} (${f.unit})`} hint={f.hint}>
              <Input type="number" min={HR_RETENTION_LIMITS[f.key][0]} max={HR_RETENTION_LIMITS[f.key][1]} value={String(draft[f.key])}
                onChange={e => setDraft({ ...draft, [f.key]: e.target.value === '' ? NaN : Number(e.target.value) })} data-testid={`hr-retention-${f.key}`} />
            </Field>
          ))}
        </div>
        <div className="mt-3"><Toggle checked={draft.auto_purge} onChange={v => setDraft({ ...draft, auto_purge: v })} label="Remove details automatically once a day when their time is up" /></div>
        {errs.length > 0 && <p className="mt-2 text-sm text-rose-700" role="alert">{errs.join('. ')}</p>}
        <div className="mt-3 flex gap-2">
          <Button size="sm" disabled={busy || !changed || errs.length > 0} onClick={() => void save()} data-testid="hr-retention-save">Save</Button>
          {changed && <Button size="sm" variant="ghost" onClick={() => setDraft(p.retention)}>Undo changes</Button>}
        </div>
        <p className="mt-3 max-w-3xl text-xs text-slate-500">UK defaults: pay and tax details 6 years after leaving, right-to-work checks 2 years, emergency contacts a month. Check with your adviser if your business needs longer.</p>
      </Card>
      <Card title="Due for removal" actions={p.due.length ? <Button size="sm" variant="danger" disabled={busy} onClick={() => void purge()} data-testid="hr-purge-now">Remove now</Button> : undefined}>
        <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800" data-testid="hr-purge-due">
          {p.due.map((i, n) => <li key={n} className="flex gap-3 py-2"><span className="font-medium">{i.display_name ?? 'Deleted items'}</span><span className="text-slate-500">{hrPurgeLine(i, p.today)}</span></li>)}
          {!p.due.length && <li className="py-2 text-slate-500">Nothing is due.</li>}
        </ul>
        {p.upcoming.length > 0 && <>
          <h3 className="mb-1 mt-4 text-sm font-semibold">Coming up in the next 90 days</h3>
          <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800" data-testid="hr-purge-upcoming">
            {p.upcoming.map((i, n) => <li key={n} className="flex gap-3 py-2"><span className="font-medium">{i.display_name ?? 'Deleted items'}</span><span className="text-slate-500">{hrPurgeLine(i, p.today)}</span></li>)}
          </ul>
        </>}
        <p className="mt-2 text-xs text-slate-500">{p.last_run ? `Last run ${when(p.last_run.at)}${p.last_run.automatic ? ' (automatic)' : ''}, ${p.last_run.removed} removed.` : 'Not run yet.'}</p>
      </Card>
    </>
  );
}

function KeysCard() {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const q = useQuery({ queryKey: hrKey(ws, 'keys'), queryFn: () => api.hr.keys(ws!), enabled: Boolean(ws), retry: false });
  const [busy, setBusy] = useState(false);
  const rotate = async () => {
    if (!(await ask({ title: 'Re-encrypt with the current key?', body: 'Bank details, NI numbers and sensitive fields encrypted with an older key are encrypted again with the current one. Nothing else changes.', confirmLabel: 'Re-encrypt', danger: false }))) return;
    setBusy(true);
    try { const r = await api.hr.rotateKeys(ws!); toast(`Re-encrypted ${r.rotated} value${r.rotated === 1 ? '' : 's'}`, 'success'); void refreshHr(ws); } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  return (
    <Card title="Encryption key" actions={q.data && q.data.needs_rotation > 0 ? <Button size="sm" variant="ghost" disabled={busy} onClick={() => void rotate()} data-testid="hr-keys-rotate">Re-encrypt now</Button> : undefined}>
      {q.isLoading ? <Spinner /> : !q.data ? <p className="text-sm text-slate-500">{errorMessage(q.error)}</p> : (
        <div className="text-sm" data-testid="hr-keys">
          <p>{hrKeyLine(q.data)}</p>
          <p className="mt-1 text-slate-500">Current key version {q.data.current_version}{q.data.counts.length > 1 ? ` · ${q.data.counts.map(c => `version ${c.version}: ${c.values}`).join(', ')}` : ''}</p>
          <EditableHint as="p" id="people.hr.key-note" className="mt-2 text-xs text-slate-500" default="The key itself is set on the server and never shown here." />
        </div>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------------------------------------ a person's record
/** On someone else's HR record: download everything (subject access) and, for leavers, erase what the law allows. */
export function HrPersonActions({ userId, name, settings, leftOn, erasedAt }: { userId: string; name: string; settings: HrSettingsView; leftOn: string | null; erasedAt: string | null }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const [erasing, setErasing] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!settings.can_subject_access && !(settings.can_retention && leftOn)) return null;
  const download = async () => {
    setBusy(true);
    try { const l = await api.hr.exportLink(ws!, userId); const a = document.createElement('a'); a.href = l.url; a.download = l.file_name; a.click(); toast('Download started', 'success'); }
    catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="hr-person-actions">
      {settings.can_subject_access && <Button size="sm" variant="ghost" disabled={busy} onClick={() => void download()} data-testid="hr-sar-download">Download all HR data</Button>}
      {settings.can_retention && leftOn && (erasedAt
        ? <span className="text-sm text-slate-500">HR data erased {hrDate(erasedAt.slice(0, 10))}</span>
        : <Button size="sm" variant="ghost" onClick={() => setErasing(true)} data-testid="hr-erase-open">Erase HR data</Button>)}
      {erasing && <EraseDialog userId={userId} name={name} onClose={() => setErasing(false)} />}
    </div>
  );
}

function EraseDialog({ userId, name, onClose }: { userId: string; name: string; onClose: () => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const plan = useHrErasure(ws, userId);
  const [confirm, setConfirm] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const p = plan.data;
  const go = async () => {
    setBusy(true);
    try { await api.hr.erase(ws!, userId, { confirm, reason: reason.trim() }); toast(`${name}’s HR data erased`, 'success'); void refreshHr(ws); onClose(); }
    catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Erase ${name}’s HR data?`} footer={p?.allowed ? <>
      <Button variant="ghost" onClick={onClose}>Cancel</Button>
      <Button variant="danger" disabled={busy || confirm !== HR_ERASE_CONFIRM || !reason.trim()} onClick={() => void go()} data-testid="hr-erase-confirm">Erase</Button>
    </> : <Button variant="ghost" onClick={onClose}>Close</Button>}>
      {plan.isLoading ? <Spinner /> : !p ? <p className="text-sm text-slate-500">{errorMessage(plan.error)}</p> : (
        <div className="space-y-3 text-sm" data-testid="hr-erase">
          {!p.allowed ? <p className="text-slate-600 dark:text-slate-300">{p.reason ?? 'This HR record can’t be erased.'}</p> : <>
            <div><h3 className="font-semibold">Removed now</h3><ul className="mt-1 list-disc pl-5 text-slate-600 dark:text-slate-300">{p.erase.map(e => <li key={e.category}>{e.label}</li>)}</ul></div>
            {p.keep.length > 0 && <div><h3 className="font-semibold">Kept because the law needs it</h3>
              <ul className="mt-1 space-y-1">{p.keep.map(k => <li key={k.category}>{k.label} <span className="text-slate-500">· until {hrDate(k.until)} · {k.why}</span></li>)}</ul>
              <p className="mt-1 text-xs text-slate-500">These are removed by the purge on that date.</p></div>}
            <Field label="Why"><Textarea rows={2} maxLength={400} value={reason} onChange={e => setReason(e.target.value)} placeholder="For example, their erasure request of 27 Oct 2026" data-testid="hr-erase-reason" /></Field>
            <Field label={`Type ${HR_ERASE_CONFIRM} to confirm`}><Input value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="off" data-testid="hr-erase-type" /></Field>
            <p className="text-xs text-slate-500">This can’t be undone.</p>
          </>}
        </div>
      )}
    </Modal>
  );
}
